import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { featureQueryViewPackage } from 'src/app/interfaces/common';
import './externalContentWebComponent.js'
import { DisplayService } from 'src/app/services/display.service';

interface externalConfig {
  url: string,
  key: string,
  parameters: {
    [key: string]: string
  }
  style: {
    horizontal: string,
    vertical: string
  }
}

@Component({
  selector: 'app-show-external-data',
  templateUrl: './show-external-data.component.html',
  styleUrls: ['./show-external-data.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  //encapsulation: ViewEncapsulation.None,
  animations: [
    /*
        A transition of void => * applies when the element enters a view, regardless of what state it assumes when entering.
        What this means: when element enters view/is rendered it goes FROM the state specified in style() below and To its
        original/natural like when no animation was defined for it. Can also use ':enter' as alias for 'void => *'
      */
     /*
      trigger("slideIn", [
        transition("void => *", [
          style({transform: "translateX(-100%)"}),
          animate("0.5s ease-out")
        ]),
      ])
      */
     trigger("slideIn", [
        state("open", style({
          //transform: "translateX(0%) translateY(0%) rotate(0turn) scale(1.0)", //Yolo entry
          transform: "translateX(0%) scale(1.0)",
          opacity: 1
        })),
        state("closed", style({
          //transform: "translateX(-100%) translateY(-100%) rotate(6turn) scale(0.5)", //Yolo entry
          transform: "translateX(-100%) scale(0.5)",
          opacity: 0
        })),
        transition("open => closed", [
          animate("0.0s ease-out")
        ]),
        transition("closed => open", [
          //animate("5.5s ease-out") //yolo entry
          animate("0.4s ease-out") 
        ])
     ])
  ]
})

/*
  Component for showing external content. That is: mouse is clicked on map -> network request is sent ->
  The response of this network request is ready made HTML which we show in mapview.
*/
export class ShowExternalDataComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() externalDataFeature: featureQueryViewPackage | null = null;

  @ViewChild("externalContentWrapper") externalContentWrapper: ElementRef | undefined = undefined;
  @ViewChild("externalContentContainer") externalContentContainer: ElementRef | undefined = undefined;
  /*@ViewChild("ecFrame") ecIframe: ElementRef*/ //externalContentIframe
  //externalHTML: any

  externalHTMLHttpOptions = {
    headers: new HttpHeaders({
      Accept: "text/html"
    }),
    "responseType": "text" as const
  }
  //Subject that fires each time a new result of a feature request is received
  featureData$ = new Subject<any>()
  externalFeatureRequest$: Observable<SafeHtml> | undefined;
  //Emits up to mapComponent, telling it to now destroy this component
  @Output() destroyExternalDataViewEE = new EventEmitter()

  animationState: "closed" | "open" = "closed";

  constructor(private http: HttpClient, public sanitizer: DomSanitizer, private displayService: DisplayService) { }

  ngOnInit() {
    this.displayService.defocusOnMap()
    //console.log("Received featureRequest data in ngOnInit: ")
    //console.log(this.externalDataFeature)
    this.externalFeatureRequest$ = this.featureData$.pipe(
      //Only continue emitting the value down the pipe if there are features here and the query view is set to external-view
      //filter( (featureData: any) => featureData?.features?.length > 0 && featureData?.metadataLayer?.query_option?.query_view === "external-view"),
      /*
        Tap taps into the stream, but otherwives leaves it untouched. Used to perform side effects, but better to use it
        as little as possible. Im using it here to set 2 CSS custom properties which control where on screen the feature-request-popup appears.
        We dont return anything from tap since featureData above will pass directly through and into the
        next map function. (But we can still access the value at this point in the pipe)
      */
      tap( (featureDataEx: featureQueryViewPackage) => {
        //New HTML coming in, reset animationsState to closed again
        this.animationState = "closed"
        //console.log(featureDataEx)
        //console.log("In tap operator, performing side effects...")
        const layerExternalConfig: externalConfig = featureDataEx?.metadataLayer?.query_option?.external_config
        //Setting the style from config as a CSS Custom properties:
        this.externalContentWrapper?.nativeElement.style.setProperty("--horizontal", layerExternalConfig?.style?.horizontal || "center")
        this.externalContentWrapper?.nativeElement.style.setProperty("--vertical", layerExternalConfig?.style?.vertical || "center")
        //this.fff = true
      }),
      //Valid feature data where the query view is set to external-view.
      map( (featureDataEx: featureQueryViewPackage) => {
        const layerExternalConfig: externalConfig = featureDataEx?.metadataLayer?.query_option?.external_config
        //console.log("Layer external config was: ")
        //console.log(layerExternalConfig)
        //Create and add queryParameters to URL from config, return fully ready URL.
        const externalURL: URL = new URL(layerExternalConfig.url)
        //console.log(externalURL)
        //console.log(externalURL.toString())
        const externalURLSearchParams: URLSearchParams = externalURL.searchParams
        //externalURLSearchParams.set("ISSI", "Gildi")
        for (const [key, value] of Object.entries(layerExternalConfig.parameters)) {
          //console.log("Key is: " + key)
          //console.log("Value is: " + value)
          externalURLSearchParams.set(key, featureDataEx?.geoJSONFeature?.features?.[0]?.properties?.[value])
        }
        //console.log(externalURL.toString())
        
        return externalURL.toString()
      }),
      switchMap( (externalReadyURL: string) => {
        return this.http.get(externalReadyURL, this.externalHTMLHttpOptions)
      }),
      //Activate the slideIn animation on the element
      tap( () => this.animationState = "open")
    )

  }

  ngAfterViewInit() {
    //Needs to be here or it always fails?!?!?!
    /*
      Need to call it here since ngOnChanges runs before ngOnInit. If we call it in ngOnChanges then there is a chance that
      ngOnInit and the template is not finished rendering, meaning the value emitted here in next never goes through the
      externalFeatureRequest$ pipe. Note: its the template that subscribes to the observable via async pipe.

      So calling it here in ngAfterViewInit we can be sure that the observable externalFeatureRequest$ is ready and it has
      already been subscribed to in the template.
    */
    this.featureData$.next(this.externalDataFeature)
    
  }

  
  ngOnDestroy(): void {
    //console.log("show-external-componet DESTROYED")
    this.displayService.focusOnMap()
  }

  closeView(): void {
    this.destroyExternalDataViewEE.emit("")
  }
}